import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import UserFormDialog from "@/components/UserFormDialog";
import { Plus, Pencil, Trash2, Shield, User as UserIcon, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/formatDate";

export default function Users() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deletingNow, setDeletingNow] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (currentUser && currentUser.role !== "admin") {
        return <Navigate to="/app" replace />;
    }

    const fmt = (d) => fmtDate(d);

    const onDelete = async () => {
        if (!deleting) return;
        setDeletingNow(true);
        try {
            await api.delete(`/users/${deleting.id}`);
            toast.success(`Removed ${deleting.name}`);
            setDeleting(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        } finally {
            setDeletingNow(false);
        }
    };

    const onResend = async (u) => {
        try {
            await api.post(`/users/${u.id}/resend-invite`);
            toast.success(`Invite resent to ${u.email}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
        }
    };

    const adminCount = users.filter((u) => u.role === "admin").length;

    return (
        <div className="p-6 lg:p-8 space-y-5 animate-fade-in" data-testid="users-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="ph-h1">Users</h1>
                    <p className="text-sm text-ph-muted mt-1">
                        Manage who can sign in and who can administer the workspace.
                    </p>
                </div>
                <Button
                    onClick={() => { setEditing(null); setFormOpen(true); }}
                    className="bg-ph-primary text-white font-medium h-9 px-3 hover:bg-ph-primaryHover"
                    data-testid="add-user-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Invite user
                </Button>
            </div>

            <div className="ph-card overflow-hidden">
                <div className="overflow-auto scrollbar-thin">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead className="bg-ph-canvas text-[11px] font-medium text-ph-muted border-b border-ph-border">
                            <tr>
                                <th className="text-left px-4 py-2.5">Name</th>
                                <th className="text-left px-4 py-2.5">Email</th>
                                <th className="text-left px-4 py-2.5">Role</th>
                                <th className="text-left px-4 py-2.5">Created</th>
                                <th className="text-right px-4 py-2.5">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={5} className="text-center py-12 text-ph-muted">Loading…</td></tr>
                            )}
                            {!loading && users.length === 0 && (
                                <tr><td colSpan={5} className="text-center py-12 text-ph-muted">No users yet.</td></tr>
                            )}
                            {users.map((u) => {
                                const isSelf = u.id === currentUser?.id;
                                const isLastAdmin = u.role === "admin" && adminCount <= 1;
                                return (
                                    <tr key={u.id} className="border-t border-ph-borderSoft hover:bg-ph-canvas" data-testid={`user-row-${u.id}`}>
                                        <td className="px-4 py-3 font-medium text-ph-text flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-ph-primarySoft text-ph-primary flex items-center justify-center text-[11px] font-semibold">
                                                {(u.name || u.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                            </div>
                                            <span>{u.name}</span>
                                            {isSelf && <span className="text-[10px] uppercase tracking-wider text-ph-mutedSoft font-semibold">You</span>}
                                            {u.pending_invite && (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold">
                                                    <Mail className="w-2.5 h-2.5" /> Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-ph-muted">{u.email}</td>
                                        <td className="px-4 py-3">
                                            {u.role === "admin" ? (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium">
                                                    <Shield className="w-3 h-3" /> Admin
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">
                                                    <UserIcon className="w-3 h-3" /> Member
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-ph-muted">{fmt(u.created_at)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-2">
                                                {u.pending_invite && (
                                                    <button
                                                        onClick={() => onResend(u)}
                                                        className="text-ph-muted hover:text-ph-primary transition-colors p-1.5 rounded hover:bg-ph-primarySoft"
                                                        data-testid={`resend-invite-${u.id}`}
                                                        title="Resend invite email"
                                                    >
                                                        <Send className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => { setEditing(u); setFormOpen(true); }}
                                                    className="text-ph-muted hover:text-ph-primary transition-colors p-1.5 rounded hover:bg-ph-primarySoft"
                                                    data-testid={`edit-user-${u.id}`}
                                                    title="Edit user"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => setDeleting(u)}
                                                    disabled={isSelf || isLastAdmin}
                                                    className="text-ph-muted hover:text-rose-600 transition-colors p-1.5 rounded hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ph-muted"
                                                    data-testid={`delete-user-${u.id}`}
                                                    title={isSelf ? "Can't delete yourself" : isLastAdmin ? "Last remaining admin" : "Delete user"}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 text-xs text-ph-muted border-t border-ph-border bg-ph-canvas">
                    {users.length} user{users.length === 1 ? "" : "s"} · {adminCount} admin{adminCount === 1 ? "" : "s"}
                </div>
            </div>

            <UserFormDialog
                open={formOpen}
                onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
                user={editing}
                onSuccess={load}
            />

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent data-testid="delete-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleting?.name} ({deleting?.email}) will lose access to the workspace immediately.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deletingNow}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="delete-user-confirm"
                        >
                            {deletingNow ? "Deleting…" : "Delete user"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
